import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import {
	useExperienceFail,
	useExperienceStart,
	useExperienceSuccess,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import type { ExperienceDetails } from '@atlassian/jira-business-experience-tracking/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagService } from '@atlassian/jira-flags/src/services';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { CategoryFieldConfig } from '../../common/types.tsx';
import type {
	JwmCreateCategory,
	JwmCreateCategoryVariables,
} from './__generated_apollo__/JwmCreateCategory';
import { CREATE_FAILED_FLAG, QUERY_NAME } from './constants.tsx';
import { CREATE_CATEGORY } from './gql.tsx';
import type { CreateProps } from './types.tsx';

export const useCreateCategory = ({
	experience,
	onCompleted,
}: {
	experience: ExperienceDetails;
	onCompleted?: (data: JwmCreateCategory) => void;
}) => {
	const { showFlag } = useFlagService();
	const project = useProject();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const startExperience = useExperienceStart(experience);
	const markExperienceSuccess = useExperienceSuccess(experience);
	const markExperienceFailed = useExperienceFail(experience);

	const projectId = String(project.id);

	const [createCategory, { data, loading, error }] = useMutation<
		JwmCreateCategory,
		JwmCreateCategoryVariables
	>(CREATE_CATEGORY, {
		onError: (apolloError) => {
			markExperienceFailed(`${experience.experience} category service error handling`, apolloError);
			showFlag(CREATE_FAILED_FLAG);

			fireErrorAnalytics({
				meta: {
					id: 'jwmCreateCategory',
					packageName: 'jiraBusinessCategories',
					teamName: 'jira-warepil',
				},
				attributes: {
					message: 'Failed to create category',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onCompleted: (onCompletedData: JwmCreateCategory) => {
			if (onCompletedData?.createJwmCategory?.errors) {
				return;
			}

			markExperienceSuccess();
			if (onCompleted) {
				onCompleted(onCompletedData);
			}
			fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`);
		},
	});

	const create = useCallback(
		async ({
			issueTypeId,
			value,
			color,
		}: CreateProps): Promise<CategoryFieldConfig | null | undefined> => {
			startExperience();

			const response = await createCategory({
				variables: {
					input: {
						projectId,
						issueTypeId,
						value,
						color,
					},
				},
			});

			const result = response?.data?.createJwmCategory;
			if (result?.success) {
				if (result.category?.option?.optionId) {
					return {
						id: result.category.id,
						color: result.category.color,
						option: {
							optionId: result.category.option.optionId,
							value: result.category.option.value,
							id: result.category.option.id,
							isDisabled: result.category.option.isDisabled,
						},
					};
				}
				return null;
			}

			const createError = result?.errors?.find((e) => e.message);
			if (createError) {
				throw new Error(createError.message || '');
			}
			return null;
		},
		[createCategory, projectId, startExperience],
	);

	// find error that has message
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const mutationBadRequestError = data?.createJwmCategory?.errors?.find((e: any) => e.message);

	return {
		error:
			// if there are any errors from Apollo, surface that first
			// otherwise if there are errors from bad request, use that
			error || mutationBadRequestError,
		data,
		loading,
		createCategory: create,
	};
};
